import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Toaster } from '../../components/ui/sonner';
import { toast } from 'sonner';
import { LayoutGrid, FileText, Briefcase, Route as RouteIcon, ClipboardCheck, CalendarCheck, ShoppingBag, LineChart, LogOut, RotateCcw, Menu, Lock } from 'lucide-react';
import { studentApi } from './journeyApi';
import { JourneyLogin } from './JourneyLogin';
import { LockedOverview, NoProfile } from './DashboardLocked';
import { Overview } from '../../components/journey/Overview';
import { CareerSummary } from '../../components/journey/CareerSummary';
import { Opportunities } from '../../components/journey/Opportunities';
import { Programs } from '../../components/journey/Programs';
import { LearningJourney } from '../../components/journey/LearningJourney';
import { Assignments } from '../../components/journey/Assignments';
import { Checkins } from '../../components/journey/Checkins';
import { Progress } from '../../components/journey/Progress';
import { MissionView } from '../../components/journey/MissionView';

const NAV = [
  ['overview', 'Overview', LayoutGrid], ['summary', 'My Career Summary', FileText], ['opportunities', 'Career Opportunities', Briefcase],
  ['learning', 'My Learning Journey', RouteIcon], ['assignments', 'Practical Assignments', ClipboardCheck], ['checkins', 'Weekly Check-ins', CalendarCheck],
  ['programs', 'Programs & Enrollment', ShoppingBag], ['progress', 'My Progress', LineChart],
];
const SECTIONS = { overview: Overview, summary: CareerSummary, opportunities: Opportunities, learning: LearningJourney,
  assignments: Assignments, checkins: Checkins, programs: Programs, progress: Progress };

const LOCK_RULES = {
  summary: 'approved',
  learning: 'enrolled', assignments: 'enrolled', checkins: 'enrolled', progress: 'enrolled',
};
const lockReason = (id, d) => {
  if (!d || id === 'overview') return '';
  if (LOCK_RULES[id] === 'approved' && !d.summary) return 'Unlocks once your consultant approves your career summary';
  if (LOCK_RULES[id] === 'enrolled' && !d.enrollments?.length) return 'Unlocks after you enrol in a programme';
  return '';
};

const Sidebar = ({ user, personas, studentId, setStudentId, section, go, onReset, onLogout, open, alerts, lockOf, studentMode }) => (
  <aside className={`${open ? 'flex' : 'hidden'} lg:flex flex-col w-full lg:w-72 lg:min-h-screen bg-[#0b1f4d] text-white p-5 lg:sticky lg:top-0 lg:h-screen`} data-testid="journey-sidebar">
    <p className="font-outfit font-bold text-lg">Elevate <span className="text-teal-300">Journey</span></p>
    <p className="text-[11px] uppercase tracking-widest text-slate-400 mt-1" data-testid="sidebar-mode-label">{personas.find((p) => p.id === studentId)?.is_demo === false ? 'Your career journey' : 'Prototype · demo data'}</p>
    {!studentMode && <>
      <label className="text-[11px] uppercase tracking-widest text-slate-400 mt-6 mb-2">Viewing profile</label>
      <select value={studentId || ''} onChange={(e) => setStudentId(e.target.value)} data-testid="persona-switcher"
        className="bg-white/10 border border-white/15 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-teal-400">
        {personas.map((p) => <option key={p.id} value={p.id} className="text-slate-900">{p.persona_label}</option>)}
      </select>
    </>}
    <nav className="mt-6 space-y-1 flex-1">
      {NAV.map(([id, label, Icon]) => (
        <button key={id} onClick={() => go(id)} data-testid={`nav-${id}`} disabled={!!lockOf(id)}
          title={lockOf(id) || undefined}
          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${section === id ? 'bg-teal-400 text-[#0b1f4d] font-semibold' : 'text-slate-300 hover:bg-white/10'} disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent`}>
          <Icon className="w-4 h-4" /> <span className="flex-1 text-left truncate">{label}</span>
          {!!lockOf(id) && <Lock className="w-3.5 h-3.5" data-testid={`nav-lock-${id}`} />}
          {id === 'opportunities' && alerts > 0 && <span className="min-w-[1.5rem] text-center text-[11px] font-bold bg-amber-400 text-[#0b1f4d] rounded-full px-1.5 py-0.5 animate-pulse" title={`${alerts} new this week`} data-testid="nav-opportunities-new-badge">{alerts}</span>}
        </button>
      ))}
    </nav>
    <div className="border-t border-white/10 pt-4 mt-4 space-y-2 text-sm">
      <p className="text-slate-400 truncate" data-testid="journey-user-email">{user.email}</p>
      {!studentMode && <button onClick={onReset} data-testid="reset-demo-btn" className="flex items-center gap-2 text-slate-300 hover:text-white"><RotateCcw className="w-4 h-4" /> Reset demo data</button>}
      <button onClick={onLogout} data-testid="journey-logout-btn" className="flex items-center gap-2 text-slate-300 hover:text-white"><LogOut className="w-4 h-4" /> Sign out</button>
    </div>
  </aside>
);

export default function JourneyStudent() {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(location.state?.user || null);
  const [authChecked, setAuthChecked] = useState(!!location.state?.user);
  const [personas, setPersonas] = useState([]);
  const [studentId, setStudentIdState] = useState(localStorage.getItem('jr_student_id'));
  const [data, setData] = useState(null);
  const [section, setSection] = useState('overview');
  const [mission, setMission] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [personasLoaded, setPersonasLoaded] = useState(false);
  const studentMode = location.pathname === '/my-journey';

  useEffect(() => {
    if (user) return;
    studentApi('/auth/me').then(setUser).catch(() => {}).finally(() => setAuthChecked(true));
  }, [user]);

  const selectStudent = (id) => { localStorage.setItem('jr_student_id', id); setStudentIdState(id); };
  const setStudentId = (id) => { selectStudent(id); setMission(null); setSection('overview'); };
  const [returnTo, setReturnTo] = useState('assignments');

  useEffect(() => {
    if (!user) return;
    studentApi(`/personas${studentMode ? '?mode=student' : ''}`).then((all) => {
      const list = all.filter((p) => (p.is_demo === false) === studentMode);
      setPersonas(list);
      setPersonasLoaded(true);
      if (list.length && !list.find((p) => p.id === studentId)) selectStudent(list[0].id);
    });
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = useCallback(() => {
    if (!studentId || !user || !personas.some((p) => p.id === studentId)) return Promise.resolve();
    return studentApi(`/me/dashboard?student_id=${studentId}`).then(setData).catch((e) => toast.error(e.message));
  }, [studentId, user, personas, studentMode]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    if (!data || data.student?.id !== studentId) return;
    const saved = localStorage.getItem(`jr_last_section_${studentId}`);
    setSection((cur) => (cur === 'overview' && saved && SECTIONS[saved] && !lockReason(saved, data) ? saved : (lockReason(cur, data) ? 'overview' : cur)));
  }, [data, studentId]);

  if (!authChecked) return <div className="min-h-screen bg-[#f7f6f2]" />;
  if (!user) return <JourneyLogin onLogin={setUser} />;

  const go = (s) => { setSection(s); setMission(null); setMenuOpen(false); window.scrollTo(0, 0); if (studentId) localStorage.setItem(`jr_last_section_${studentId}`, s); };
  const openMission = (id) => { setReturnTo(section); setMission(id); window.scrollTo(0, 0); };
  const onReset = async () => {
    const list = await studentApi('/personas/reset', { method: 'POST' });
    const demos = list.filter((p) => p.is_demo !== false);
    setPersonas(demos); setStudentId(demos[0].id); toast.success('Demo data reset');
  };
  const onLogout = async () => { await studentApi('/auth/logout', { method: 'POST' }); setUser(null); navigate(window.location.pathname); };
  if (studentMode && personasLoaded && !personas.length) return <NoProfile onLogout={onLogout} />;
  const locked = !!data?.locked;
  const Section = locked ? LockedOverview : SECTIONS[section];
  const ctx = { data, refresh, go, openMission, studentId };

  return (
    <div className="min-h-screen bg-[#f7f6f2] lg:flex font-inter text-slate-800" data-testid="journey-student-app">
      <Toaster position="top-center" richColors />
      <div className="lg:hidden flex items-center justify-between bg-[#0b1f4d] text-white px-5 py-4">
        <p className="font-outfit font-bold">Elevate <span className="text-teal-300">Journey</span></p>
        <button onClick={() => setMenuOpen(!menuOpen)} data-testid="journey-mobile-menu-btn"><Menu className="w-5 h-5" /></button>
      </div>
      <Sidebar {...{ user, personas, studentId, setStudentId, go, onReset, onLogout, studentMode }} lockOf={(id) => lockReason(id, data)} section={locked ? 'overview' : section} open={menuOpen} alerts={data?.opportunity_alerts?.total_new || 0} />
      <main className="flex-1 px-5 sm:px-8 lg:px-12 py-8 lg:py-12 max-w-7xl" data-testid={`section-${locked ? 'overview' : section}`}>
        {!data ? <div className="h-40 rounded-2xl bg-white animate-pulse" /> :
          mission && !locked ? <MissionView key={mission} missionId={mission} {...ctx} onBack={() => { setMission(null); setSection(returnTo); }} /> : <Section {...ctx} />}
      </main>
    </div>
  );
}
